import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FeedbackApiBase } from './feedback-api-base';
import {
  AdvancedReviewRequest,
  AiReviewAck,
  AskQuestionRequest,
  AskQuestionResponse,
  BasicReviewRequest,
  CopyCheckAck,
  CopyCheckRequest,
  GuidedReviewRequest,
  LinkCheckAck,
  LinkCheckRequest,
} from '../models';

/**
 * Mirrors App\Http\Controllers\AIFeedbackController.
 * The review endpoints kick off asynchronous work and return an acknowledgment;
 * the generated feedback is fetched later via {@link FeedbackService}.
 * All endpoints live under `/api/v1`.
 */
@Injectable({ providedIn: 'root' })
export class AiFeedbackService extends FeedbackApiBase {
  /** POST /v1/ai/basic-review */
  basicReview(request: BasicReviewRequest): Observable<AiReviewAck> {
    return this.http.post<AiReviewAck>(this.url('ai/basic-review'), request);
  }

  /** POST /v1/ai/advanced-review */
  advancedReview(request: AdvancedReviewRequest): Observable<AiReviewAck> {
    return this.http.post<AiReviewAck>(this.url('ai/advanced-review'), request);
  }

  /** POST /v1/ai/guided-review */
  guidedReview(request: GuidedReviewRequest): Observable<AiReviewAck> {
    return this.http.post<AiReviewAck>(this.url('ai/guided-review'), request);
  }

  /**
   * POST /v1/ai/link-check — checks one page, or every sitemap page, for broken
   * links. Runs in the background; 404/410 links arrive as Broken links feedback.
   */
  linkCheck(request: LinkCheckRequest): Observable<LinkCheckAck> {
    return this.http.post<LinkCheckAck>(this.url('ai/link-check'), request);
  }

  /**
   * POST /v1/ai/copy-check — proofreads one page, or sitemap pages, for spelling
   * and grammar in the English the user named. Runs in the background; each issue
   * arrives as Spelling & grammar feedback.
   */
  copyCheck(request: CopyCheckRequest): Observable<CopyCheckAck> {
    return this.http.post<CopyCheckAck>(this.url('ai/copy-check'), request);
  }

  /** POST /v1/ai/question */
  askQuestion(question: string): Observable<AskQuestionResponse> {
    const request: AskQuestionRequest = { question };
    return this.http.post<AskQuestionResponse>(this.url('ai/question'), request);
  }
}
